import { create } from 'zustand'
import {
  DEFAULT_REPLAY_TIME,
  REPLAY_TIME_PRESETS,
  parseClockText,
  type ReplayTimeSelection,
} from './replay-time'

/**
 * The replay start time the user last chose, kept as a single app-wide
 * preference. A trader who rehearses pre-market opens wants EVERY replay to
 * start there, including the ones launched from the chart's right-click
 * "Replay from this day", which used to hardcode 09:30.
 *
 * One bounded key, no per-panel fan-out (see the localStorage quota rule).
 *
 * Spec: docs/superpowers/specs/2026-08-27-chart-replay-calendar-design.md
 */
const REPLAY_TIME_KEY = 'edge:chart:replay-time'

function isValid(value: unknown): value is ReplayTimeSelection {
  if (!value || typeof value !== 'object') return false
  const { preset, custom } = value as Partial<ReplayTimeSelection>
  if (typeof custom !== 'string') return false
  if (preset === 'custom') return parseClockText(custom) != null
  return REPLAY_TIME_PRESETS.some((p) => p.id === preset)
}

function readPersisted(): ReplayTimeSelection {
  try {
    const raw = localStorage.getItem(REPLAY_TIME_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (isValid(parsed)) return parsed
    }
  } catch {
    /* ignore malformed / unavailable storage */
  }
  return DEFAULT_REPLAY_TIME
}

function persist(selection: ReplayTimeSelection): void {
  try {
    localStorage.setItem(REPLAY_TIME_KEY, JSON.stringify(selection))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

interface ReplayPrefsState {
  replayTime: ReplayTimeSelection
  setReplayTime: (selection: ReplayTimeSelection) => void
}

export const useReplayPrefs = create<ReplayPrefsState>((set) => ({
  replayTime: readPersisted(),
  setReplayTime: (selection) => {
    persist(selection)
    set({ replayTime: selection })
  },
}))

/** Non-reactive read for callbacks (right-click handlers, action dispatch). */
export function currentReplayTime(): ReplayTimeSelection {
  return useReplayPrefs.getState().replayTime
}
